// BrokerDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../axios';
import config from '../../config';
import './BrokerDashboard.css';
import '../Seller/unifiedFileUpload.css'; // Import the unified upload CSS
import { useLanguage } from '../../i18n/LanguageContext';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';

// Add Mapbox token from config
const MAPBOX_TOKEN = config.mapbox_token;

const BrokerDashboard = () => {
  const [description, setDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyChain, setCompanyChain] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [bevakning, setBevakning] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [companyEditMode, setCompanyEditMode] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Add state for address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const fileInputRef = useRef(null);
  const backend_url = config.backend_url;
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchBrokerDescription();
    fetchBrokerCompanyInfo();
    fetchProfileImage();
  }, []);

  // Ensure we have auth token in headers for all requests
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBrokerDescription = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/broker/description`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setDescription(response.data.description || '');
    } catch (error) {
      console.error('Error fetching broker description:', error);
    }
  };

  const fetchBrokerCompanyInfo = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/broker/company-info`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCompanyName(response.data.fmi_company_name || '');
      setCompanyAddress(response.data.fmi_company_address || '');
      setCompanyChain(response.data.fmi_company_chain || '');
      setEmail(response.data.email || '');
      setPhoneNumber(response.data.phone_number || '');
      setBevakning(response.data.bevakning || 0);
      setLatitude(response.data.latitude || null);
      setLongitude(response.data.longitude || null);
    } catch (error) {
      console.error('Error fetching broker company info:', error);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/broker/profile-image`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      if (response.data.image_url) {
        setProfileImage(response.data.image_url);
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleCompanyEditClick = () => {
    setCompanyEditMode(true);
  };

  const handleSaveClick = async () => {
    try {
      await axios.post(
        `${backend_url}/api/broker/description`,
        { description },
        { 
          headers: getAuthHeaders(),
          withCredentials: true 
        }
      );
      setEditMode(false);
    } catch (error) {
      console.error('Error updating broker description:', error);
    }
  };

  const handleCompanySaveClick = async () => {
    try {
      await axios.post(
        `${backend_url}/api/broker/company-info`,
        { 
          fmi_company_name: companyName,
          fmi_company_address: companyAddress,
          fmi_company_chain: companyChain,
          email: email,
          phone_number: phoneNumber,
          bevakning: bevakning,
          latitude: latitude,
          longitude: longitude
        },
        { 
          headers: getAuthHeaders(),
          withCredentials: true 
        }
      );
      setCompanyEditMode(false);
    } catch (error) {
      console.error('Error updating broker company info:', error);
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleCompanyNameChange = (event) => {
    setCompanyName(event.target.value);
  };

  const handleCompanyAddressChange = async (event) => {
    const value = event.target.value;
    setCompanyAddress(value);
    
    // Only search if the value is sufficiently long
    if (value.length > 3) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            value
            )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleCompanyChainChange = (event) => {
    setCompanyChain(event.target.value);
  };

  const handleBevakningChange = (event) => {
    setBevakning(parseInt(event.target.value));
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  // Drag and drop handling
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleProfileImageChange(e.dataTransfer.files[0]);
    }
  }, []);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleProfileImageChange(event.target.files[0]);
    }
  };

  const handleProfileImageChange = (file) => {
    if (file) {
      setNewProfileImage(file);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const handleProfileImageUpload = async () => {
    if (!newProfileImage) return;

    setLoading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', newProfileImage);

    try {
      await axios.post(`${backend_url}/api/broker/profile-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      fetchProfileImage(); // Refresh the image
      setNewProfileImage(null);
      setPreviewUrl(null);
    } catch (error) {
      console.error('Error uploading profile image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageDelete = async () => {
    try {
      await axios.delete(`${backend_url}/api/broker/profile-image`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setProfileImage(null);
      setNewProfileImage(null);
      setPreviewUrl(null);
    } catch (error) {
      console.error('Error deleting profile image:', error);
    }
  };

  // Add handler for suggestion selection
  const handleSuggestionClick = (feature) => {
    setCompanyAddress(feature.place_name);
    // Mapbox returns [lng, lat]
    setLongitude(feature.center[0]);
    setLatitude(feature.center[1]);
    setAddressSuggestions([]);
  };

  return (
    <div className="broker-dashboard">
      <div className="dashboard-header">
        <h1>{t('brokerDashboard.title')}</h1>
        <p className="dashboard-subtitle">{t('brokerDashboard.subtitle')}</p>
      </div>
      
      <div className="dashboard-grid">
        {/* Profile Image Upload Section */}
        <div className="dashboard-card profile-image-section">
          <div className="card-header">
            <h3>{t('brokerDashboard.profileImage')}</h3>
            <p className="section-description">{t('brokerDashboard.profileHelp')}</p>
          </div>
          
          <div className="unified-upload-container">
            {/* Current profile image display */}
            {profileImage && !previewUrl && (
              <div className="current-profile-container">
                <div className="current-profile-image">
                  <img src={profileImage} alt={t('brokerDashboard.profileImage')} className="profile-image" />
                </div>
                <button onClick={handleProfileImageDelete} className="delete-button">
                  <i className="fas fa-trash"></i> {t('brokerDashboard.deleteImage')}
                </button>
              </div>
            )}
            
            {/* No profile image or preview state */}
            {!profileImage && !previewUrl && (
              <div 
                className={`drop-area ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={triggerFileInput}
              >
                <div className="profile-image-container">
                  <div className="no-profile-image">
                    <i className="fas fa-user-circle"></i>
                    <span>{t('brokerDashboard.noProfileImage')}</span>
                  </div>
                </div>
                <p className="drop-text">
                  {t('brokerDashboard.clickDrop')}
                </p>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileInputChange} 
                  style={{ display: 'none' }}
                  accept="image/*"
                />
              </div>
            )}
            
            {/* Preview of new profile image */}
            {previewUrl && (
              <div className="broker-image-preview" style={{ width: "100%", padding: "20px", height: "auto", minHeight: "300px" }}>
                <img 
                  src={previewUrl} 
                  alt={t('brokerDashboard.preview')} 
                  className="preview-image" 
                  style={{ width: "180px", height: "180px", objectFit: "cover", borderRadius: "50%" }}
                />
                <div className="preview-actions" style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "20px", width: "100%" }}>
                  <button 
                    onClick={() => { setPreviewUrl(null); setNewProfileImage(null); }} 
                    className="cancel-preview-btn"
                    style={{ padding: "10px 20px", fontSize: "16px", minWidth: "120px" }}
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={handleProfileImageUpload} 
                    className="upload-button" 
                    disabled={loading}
                    style={{ padding: "10px 20px", fontSize: "16px", minWidth: "120px" }}
                  >
                    {loading ? t('brokerDashboard.uploading') : t('brokerDashboard.upload')}
                  </button>
                </div>
                
                {loading && (
                  <div className="progress-container" style={{ width: "80%", maxWidth: "300px", margin: "20px auto" }}>
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                    <span className="progress-text">{uploadProgress}%</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Company Information Section */}
        <div className="dashboard-card company-info-section">
          <div className="card-header">
            <h3>{t('brokerDashboard.companyInfo')}</h3>
            <p className="section-description">{t('brokerDashboard.companyInfoHelp')}</p>
          </div>
          
          {companyEditMode ? (
            <div className="edit-fields">
              <div className="form-group">
                <label>{t('brokerDashboard.companyName')}:</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={handleCompanyNameChange}
                  placeholder={t('brokerDashboard.companyName')}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>{t('brokerDashboard.companyAddress')}:</label>
                <input
                  type="text"
                  value={companyAddress}
                  onChange={handleCompanyAddressChange}
                  placeholder={t('brokerDashboard.companyAddress')}
                  className="form-input"
                />
                {/* Add suggestions dropdown */}
                {addressSuggestions.length > 0 && (
                  <ul className="suggestions-list">
                    {addressSuggestions.map((feature) => (
                      <li
                        key={feature.id}
                        onClick={() => handleSuggestionClick(feature)}
                      >
                        {feature.place_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="form-group">
                <label>{t('brokerDashboard.chain')}:</label>
                <input
                  type="text"
                  value={companyChain}
                  onChange={handleCompanyChainChange}
                  placeholder={t('brokerDashboard.chain')}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>{t('common.email')}:</label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={t('common.email')}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>{t('brokerDashboard.phone')}:</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder={t('brokerDashboard.phone')}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>{t('brokerDashboard.monitoring')}:</label>
                <select 
                  value={bevakning} 
                  onChange={handleBevakningChange}
                  className="form-select"
                >
                  <option value={0}>{t('brokerDashboard.noMonitoring')}</option>
                  <option value={1}>{t('brokerDashboard.allObjects')}</option>
                  <option value={2}>{t('brokerDashboard.cityObjects')}</option>
                </select>
              </div>
              <button onClick={handleCompanySaveClick} className="save-button">
                <i className="fas fa-save"></i> {t('brokerDashboard.save')}
              </button>
            </div>
          ) : (
            <div className="view-fields">
              <div className="info-row">
                <span className="info-label">{t('brokerDashboard.companyName')}:</span>
                <span className="info-value">{companyName ? companyName : t('brokerDashboard.missingInfo')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('brokerDashboard.companyAddress')}:</span>
                <span className="info-value">{companyAddress ? companyAddress : t('brokerDashboard.missingInfo')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('brokerDashboard.chain')}:</span>
                <span className="info-value">{companyChain ? companyChain : t('brokerDashboard.missingInfo')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('common.email')}:</span>
                <span className="info-value">{email ? email : t('brokerDashboard.missingInfo')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('brokerDashboard.phone')}:</span>
                <span className="info-value">{phoneNumber ? phoneNumber : t('brokerDashboard.missingInfo')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">{t('brokerDashboard.monitoring')}:</span>
                <span className="info-value">{
                  bevakning === 0 ? t('brokerDashboard.noMonitoring') : 
                  bevakning === 1 ? t('brokerDashboard.allObjects') : 
                  bevakning === 2 ? t('brokerDashboard.cityObjects') : t('brokerDashboard.missingInfo')
                }</span>
              </div>
              <button onClick={handleCompanyEditClick} className="edit-button">
                <i className="fas fa-edit"></i> {t('brokerDashboard.edit')}
              </button>
            </div>
          )}
        </div>
      
        {/* Broker Description Section */}
        <div className="dashboard-card description-section">
          <div className="card-header">
            <h3>{t('brokerDashboard.description')}</h3>
            <p className="section-description">{t('brokerDashboard.descriptionHelp')}</p>
          </div>
          
          {editMode ? (
            <div className="edit-fields">
              <textarea
                value={description}
                onChange={handleDescriptionChange}
                rows={6}
                placeholder={t('brokerDashboard.descriptionPlaceholder')}
                className="form-textarea"
              />
              <button onClick={handleSaveClick} className="save-button">
                <i className="fas fa-save"></i> {t('brokerDashboard.save')}
              </button>
            </div>
          ) : (
            <div className="view-fields">
              {description ? (
                <p className="description-text">{description}</p>
              ) : (
                <p className="no-data">{t('brokerDashboard.noDescription')}</p>
              )}
              <button onClick={handleEditClick} className="edit-button">
                <i className="fas fa-edit"></i> {t('brokerDashboard.edit')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrokerDashboard;
